/**
 * client/src/lib/api.ts — the one place a fetch becomes either data or a typed refusal.
 *
 * Every route is `/api/v1/…` (§10). `credentials: "same-origin"` and nothing else: the session is
 * the signed httpOnly `abk` cookie (§6.1), so there is no token to attach and nothing for XSS to
 * read. Lifted in shape from books.karti.ai `client/src/lib/api.ts`, with two additions this product
 * needs and that one did not:
 *
 *   1. `ApiError.reason` — the server's *machine* reason, kept separate from the sentence we show a
 *      human. §3.7 answers a scanned PDF with `422 {error:"scanned-pdf-unsupported"}`; that is a code,
 *      not prose, and rendering it raw is how a user reads "scanned-pdf-unsupported" and learns
 *      nothing. `refusalCopy()` below is the single table that turns a code into "what it refused and
 *      what would fix it" — AGENTS.md's refusal rule, in one place, so the Upload screen's *local*
 *      pre-flight and the server's answer are worded identically. A code we do not know renders the
 *      server's own string, because an unknown refusal shown verbatim beats a generic one.
 *   2. `uploadWithProgress` — a book is up to 512 MB and `fetch` cannot report upload progress
 *      (there is no readable upload stream in Safari). One XHR, and the user sees the bytes move.
 */

import type { Actor } from "../../../shared/actor.ts";
import type {
  BookKind,
  BookMeta,
  BookVisibility,
  Chapter,
  Edition,
  EditionState,
  EditionStructure,
} from "../../../shared/doc.ts";
import type { JobLane } from "../../../shared/config.ts";

const BASE = "/api/v1";

// ─── errors ──────────────────────────────────────────────────────────────────

/**
 * A non-2xx, a network failure, or a body that wasn't JSON.
 *
 * `status === 0` means the request never reached the server. That is a different thing from a 500 and
 * the UI says so — "you're offline" and "the server is broken" send a person to two different places.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** The server's `error` field verbatim: a code like `scanned-pdf-unsupported`, or a sentence. */
    readonly reason: string,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** The request never got an answer. Not the same as a 5xx. */
  get offline(): boolean {
    return this.status === 0;
  }

  /**
   * §6.4 / LAW 17: book-scoped routes refuse **404, never 403**, because a 403 confirms a book exists
   * at that id. So a 404 from a book route means "no, or not for you" and the UI must not distinguish.
   */
  get notFound(): boolean {
    return this.status === 404;
  }

  /** LAW 16's shape at the UI layer: the auth provider being down is never spelled "wrong password". */
  get upstreamDown(): boolean {
    return this.status === 503;
  }
}

/**
 * The refusal table. Codes come from `worker/src/ingest/probe.ts` and the upload route; that file is
 * authoritative for the set, this one is authoritative for the words.
 *
 * "A refusal must name what it refused and what would fix it" — AGENTS.md. "Couldn't process this
 * file" is how someone re-uploads the same file four times.
 */
const REFUSALS: Record<string, { title: string; fix?: string }> = {
  "mobi-unsupported": {
    title: "Kindle files (.mobi, .azw3) can't be read directly.",
    fix: "Convert with Calibre to EPUB first, then upload the .epub.",
  },
  "scanned-pdf-unsupported": {
    title: "This PDF is a scan — it has pictures of pages, not text.",
    fix: "There is nothing to read or narrate yet. If you have an EPUB or a text-layer PDF of the same book, upload that instead.",
  },
  "drm-protected": {
    title: "This EPUB is DRM-protected.",
    fix: "Only the store it came from can open it. A DRM-free copy of the same book will work.",
  },
  "fixed-layout-unsupported": {
    title: "This is a fixed-layout EPUB — a comic or a picture book with pages baked as images.",
    fix: "The reflowing reader can't page it honestly, so it is refused rather than half-shown.",
  },
  "unsupported-format": {
    title: "That file type isn't one we can read.",
    fix: "EPUB, plain text and HTML work today.",
  },
  "file-too-large": {
    title: "That file is over the per-file limit.",
    fix: "512 MB per file. A book this size is usually a scan — an EPUB of the same title is a few megabytes.",
  },
  "quota-exceeded": {
    title: "That would put you over your storage.",
    fix: "Delete a book you no longer want, or ask Karti to raise the limit.",
  },
  "too-many-books": {
    title: "You're at the book limit for one account.",
    fix: "Delete a book you no longer want, or ask Karti to raise the limit.",
  },
  "invite-required": {
    title: "This site is invite-only.",
    fix: "You need the invite code to make an account. Ask Karti for it.",
  },
  "invite-invalid": {
    title: "That invite code isn't right.",
    fix: "Check it for a stray space, and mind the case.",
  },
  "email-taken": {
    title: "There is already an account on that email.",
    fix: "Sign in with the password you use on the other karti.ai sites — it is one account across all of them.",
  },
  "handle-taken": {
    title: "That username is taken.",
    fix: "Pick another. 2–24 characters, letters, digits, dot, dash or underscore.",
  },
  "no-session": {
    title: "Your session hasn't been established yet.",
    fix: "Give it a second and try again — this is a boot race, not a sign-out.",
  },

  // ── P4 (§6.4, §6.5) ──────────────────────────────────────────────────────
  "owner-quota-exceeded": {
    // ⚠️ Deliberately does NOT blame the reader. §6.5 moved the narration budget onto the book's
    // owner — "the owner published it, the owner pays" — so the person who sees this is usually
    // somebody who was sent a link and can do precisely nothing about it. Saying "you are over your
    // limit" to them is both false and a dead end.
    title: "This book's owner is out of narration storage.",
    fix: "Pages that are already narrated still play. New ones can't be generated until they free some up.",
  },
  "taken-down": {
    title: "This book has been taken down.",
    fix: "It can't be shared while that stands. Ask Karti to lift it.",
  },
  "too-many-links": {
    title: "This book already has as many live links as it's allowed.",
    fix: "Revoke one you no longer need, and make another.",
  },
  "regenerate-forbidden": {
    // §0 decision 1, in the words a person reads. Not "forbidden": the report they can still file is
    // the whole point of the narrowing, and it writes the identical telemetry row.
    title: "Only the book's owner can regenerate a sentence.",
    fix: "“Sounds wrong” still works and goes to the same place — it is what tells Karti which sentences to fix.",
  },
  "airplay-share-link": {
    title: "AirPlay isn't available on a book you're reading through a share link.",
    fix: "It plays on this device. Sign in as the owner, or ask them to make the book unlisted.",
  },
};

/**
 * Turn a refusal into something a person can act on. Returns `null` when the reason is not a code we
 * know, so the caller falls back to the server's own sentence rather than to a generic one.
 */
export function refusalCopy(reason: string): { title: string; fix?: string } | null {
  return REFUSALS[reason] ?? null;
}

// ─── the request ─────────────────────────────────────────────────────────────

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);

  /**
   * JSON by default — but **never for a `FormData` body**. `fetch` writes
   * `multipart/form-data; boundary=…` itself, and only if nothing else set the header. Setting JSON
   * here sends an upload with a multipart body and no boundary, and the server's JSON parser 500s on
   * `--WebKitFormBoundary…`. (books.karti.ai shipped exactly this, and its smoke suite never caught
   * it because the suite posts multipart with plain `fetch`.)
   */
  if (init.body && !headers.has("content-type") && !(init.body instanceof FormData)) {
    headers.set("content-type", "application/json");
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { credentials: "same-origin", ...init, headers });
  } catch {
    // A rejected fetch is a transport failure, never a server answer. Status 0 keeps the two apart
    // everywhere downstream — the reader must be able to say "you're offline" and mean it.
    throw new ApiError(0, "network", "Can't reach the server. Check your connection.");
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    const reason = pickString(body, "error") ?? `http-${res.status}`;
    const known = refusalCopy(reason);
    const message =
      known?.title ??
      pickString(body, "message") ??
      (reason.startsWith("http-") ? `Something went wrong (${res.status}).` : reason);
    throw new ApiError(res.status, reason, message, body);
  }

  if (body === null && text) {
    // A non-JSON 200 means the SPA fallback served index.html to a fetch — almost always a route the
    // server doesn't have. Say that, rather than "Unexpected token <".
    throw new ApiError(
      res.status,
      "not-json",
      "The server answered with a page instead of data — that endpoint probably isn't built yet.",
    );
  }
  return body as T;
}

function pickString(body: unknown, key: string): string | null {
  if (body && typeof body === "object" && key in body) {
    const v = (body as Record<string, unknown>)[key];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return null;
}

export const api = {
  get: <T>(path: string, init?: RequestInit) => request<T>(path, init),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  /** Multipart. Never touch content-type here — the boundary is a random string only `fetch` knows. */
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: "POST", body: form }),

  /**
   * Multipart with a progress callback, over XHR.
   *
   * `fetch` cannot report *upload* progress: request streams are not available in Safari, which is
   * the primary browser for this product. A 300 MB EPUB going up a residential uplink with a
   * spinner and no number is indistinguishable from a hang, and the user cancels a good upload.
   *
   * Returns an object carrying `abort()` so the screen can offer Cancel and mean it.
   */
  uploadWithProgress<T>(
    path: string,
    form: FormData,
    onProgress: (sent: number, total: number) => void,
  ): { promise: Promise<T>; abort: () => void } {
    const xhr = new XMLHttpRequest();
    const promise = new Promise<T>((resolve, reject) => {
      xhr.open("POST", `${BASE}${path}`);
      xhr.withCredentials = true; // same-origin already sends the cookie; explicit costs nothing.
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total);
      };
      xhr.onerror = () => reject(new ApiError(0, "network", "Can't reach the server. Check your connection."));
      xhr.onabort = () => reject(new ApiError(0, "aborted", "Upload cancelled."));
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : null;
        } catch {
          body = null;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(body as T);
          return;
        }
        const reason = pickString(body, "error") ?? `http-${xhr.status}`;
        const known = refusalCopy(reason);
        reject(
          new ApiError(
            xhr.status,
            reason,
            known?.title ??
              pickString(body, "message") ??
              (reason.startsWith("http-") ? `Upload failed (${xhr.status}).` : reason),
            body,
          ),
        );
      };
      xhr.send(form);
    });
    return { promise, abort: () => xhr.abort() };
  },
};

// ─── the shapes the screens read ─────────────────────────────────────────────
//
// These are the client's view of §10. They live here rather than in `shared/` because they are HTTP
// projections, not the document model — `shared/doc.ts` owns the model and is imported above for
// every field that *is* part of it, so a change to `BookMeta` is a type error here rather than a
// silently stale copy.

/**
 * `GET /api/v1/session` — awaited before anything that needs an actor (§6.1). `Cache-Control:
 * no-store`, and the only route besides `/s/:token` that may mint the guest cookie (LAW 15).
 */
export interface SessionResponse {
  actor: Actor | null;
  /** Present for signed-in users. Guests have an `actor` and no `user`. */
  user?: SessionUser | null;
}

export interface SessionUser {
  id: string;
  handle: string;
  displayName: string;
  email: string;
  isAdmin: boolean;
}

/** `GET /api/v1/me` — profile, quota, usage. Signed-in only. */
export interface MeResponse {
  user: SessionUser;
  quotaBytes: number;
  usedBytes: number;
  bookCount: number;
  maxBooks: number;
  maxUploadBytes: number;
}

/**
 * One card on the shelf (§6.5).
 *
 * It satisfies `AccessBook` structurally (`ownerId`, `visibility`, and the two nullable timestamps),
 * which is the point: the shelf filters its own rows through `canList()` from `shared/access.ts`
 * rather than trusting a boolean the server computed (LAW 13). No `percent` field — progress is
 * `position.ord / sentenceCount`, computed from two numbers that are already part of the model, so
 * there is no second definition of "how far in" to drift.
 */
export interface ShelfCard {
  id: string;
  ownerId: string;
  title: string;
  author: string | null;
  visibility: BookVisibility;
  takenDownAt?: number | null;
  deletedAt?: number | null;
  /** A live named-account read grant for this caller. It does not make the book editable. */
  directAccess?: boolean;
  kind: BookKind;
  coverSha: string | null;
  createdAt: number;
  currentEditionId: string | null;
  /** State of the current edition. `null` when the book has no edition yet (the job hasn't started). */
  editionState: EditionState | null;
  /** `editions.structureConfirmedAt === null` — drives the "N books need confirming" row (§3.4). */
  needsConfirm: boolean;
  /**
   * The current edition, as the shelf needs it. `bodyStartConfidence` is what separates a book the
   * importer would stake something on from one it guessed at, and therefore which of them may be
   * confirmed in bulk (§3.4).
   */
  edition?: {
    id: string;
    bodyStartConfidence: number | null;
    structureConfirmedAt: number | null;
    needsReview: boolean;
  } | null;
  sentenceCount: number;
  chapterCount: number;
  position: {
    sid: string;
    ord: number;
    chapterOrd: number;
    chapterTitle: string | null;
    updatedAt: number;
  } | null;
  /** Narration coverage. `null` before any page exists. */
  narration: NarrationCoverage | null;
}

/**
 * How much of a book has a voice — `server/src/narration/coverage.ts`, the ONE definition.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ⚠️ **THE DENOMINATOR IS `pagesAudible`, NEVER `pagesTotal`, AND THAT IS NOT A REFINEMENT.**
 *
 * `pagesTotal` counts every page of the edition, including the ones `planPage` refuses with
 * `nothing-to-say` — a title page, a colophon, a chapter-heading page with no prose under it. No GPU
 * can ever close that gap, so a bar drawn against `pagesTotal` **can never reach the end**. Measured
 * against the live shelf on 2026-08-08: *The Iliad* 71 unspeakable pages of 569, *Self-Help* 27 of
 * 379, *Alice* 15 of 104 — **not one book in the library could have reached 100 % by being narrated**,
 * and the same arithmetic had already shipped an `.m4b` whose own `comment` tag told a car stereo the
 * book was unfinished.
 *
 * `pagesAudible` is the union rule: a page counts if it has something to say **or** already has
 * audio. The second half matters — a page whose prose was deleted after it was narrated still has a
 * file somebody can play, and dropping it from the denominator alone would print 12 of 11.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `voiceId` rides along because narration state is per-voice: the same book is silent in one voice
 * and complete in another, and a chip that does not name the voice is one that will be wrong the day
 * a second voice exists.
 */
export interface NarrationCoverage {
  /** Pages that have an audio artifact. The numerator, always. */
  pagesWithAudio: number;
  /** Pages that could have one. The denominator, always. */
  pagesAudible: number;
  /** Every page of the edition. Shown as *"and 15 with nothing to read aloud"*, never as a divisor. */
  pagesTotal: number;
  voiceId?: string;
  /**
   * @deprecated The pre-P7 spelling, still sent by `books.ts` and `shelf.ts` carrying the **corrected**
   * pair (`readyPages = pagesWithAudio`, `totalPages = pagesAudible`). Nothing in this client reads
   * them any more; they exist so a browser holding a cached bundle across the deploy does not divide
   * by a field that vanished. Delete both sides together, server first.
   */
  readyPages?: number;
  /** @deprecated See `readyPages`. */
  totalPages?: number;
}

export interface ShelfResponse {
  books: ShelfCard[];
  /**
   * §6.5's "N books need confirming" figure, over the whole library rather than the current page.
   *
   * The shelf renders its own count from the rows it is actually showing and can actually edit, so
   * the badge can never disagree with the list under it. This field stays in the contract because
   * it is what the server computes and it is the number a paginated shelf would need.
   */
  needsConfirming: number;
}

export type ShelfScope = "mine" | "public" | "all";
export type ShelfSort = "recent" | "title";

/** `GET /api/v1/books/:id`. */
export interface BookDetail {
  book: BookMeta;
  edition: Edition | null;
  chapterCount: number;
  pageCount: number;
  narration: NarrationCoverage | null;
  /**
   * How this request got in (§6.6). It is the field that tells the screen it is looking at somebody
   * else's book through a link rather than at its own — and `"share"` is the only value that means
   * "you are a guest here", which is what the reader needs in order to offer the right things.
   */
  access?: { via: "god" | "owner" | "public" | "unlisted" | "grant" | "share" | "none" };
  owner?: { id: string; handle: string; displayName: string } | null;
}

// ─── sharing (§6.4) ──────────────────────────────────────────────────────────

/**
 * One share link, as its owner sees it.
 *
 * ⚠️ `token` and `url` are here in full, and only ever sent to somebody who passed `canEdit` on the
 * book. An owner who cannot see the link cannot send it to anybody — that is the whole screen. What
 * must not happen is the reverse: this must never reach a log, which is why `GET /s/:token` runs at
 * `logLevel: "warn"` and rewrites its own URL before any hook can serialise it.
 */
export interface ShareLink {
  token: string;
  url: string;
  bookId: string;
  label: string | null;
  allowDownload: boolean;
  /** §0 decision 1's per-link escape hatch. Defaults to false at the server; see `VisibilityMenu`. */
  allowRegenerate: boolean;
  allowRoom: boolean;
  expiresAt: number | null;
  revokedAt: number | null;
  useCount: number;
  lastUsedAt: number | null;
  createdAt: number;
  createdBy: string;
  /** Computed by the server so "live" has one definition — revoked *and* expired is one dead link. */
  live: boolean;
  expired: boolean;
}

export interface SharesResponse {
  bookId: string;
  links: ShareLink[];
  limit: number;
  liveCount: number;
}

export interface CreateShareBody {
  label?: string;
  /** Absolute milliseconds. The screen computes it from a number of days; the wire carries one unit. */
  expiresAt?: number | null;
  allowDownload?: boolean;
  allowRegenerate?: boolean;
  allowRoom?: boolean;
}

/** `PATCH /api/v1/books/:id`. `publicRequested` is true when `public` became a request, not a fact. */
export interface PatchBookResult {
  book: BookMeta;
  publicRequested: boolean;
  /** Room members ejected because the visibility change took the book away from them (§6.4). */
  evicted?: number;
}

/**
 * `GET /api/v1/books/:id/start` — the saved position, else `bodyStart` (§3.4: one column, two
 * consumers). `source` is what the reader shows in the resume chip.
 */
export interface StartPoint {
  /** Null while the book is still ingesting — `source: "pending"`. */
  editionId: string | null;
  sid: string | null;
  chapterOrd?: number;
  /**
   * ⚠️ Five values, and they are `routes/position.ts`'s, verbatim. This used to declare two
   * (`"position" | "bodyStart"`), neither of them spelled the way the route spells them — so the one
   * state LAW 2 needs the UI to distinguish, `"position-remapped"` ("your place moved because the
   * book was re-imported"), was not even expressible. `"pending"` is a book whose ingest has not
   * landed; it is a 200, not a 404, because the shelf just linked to it.
   */
  source: "position" | "position-remapped" | "body-start" | "first-sentence" | "pending";
}

/**
 * `POST /api/v1/uploads` — multipart, streamed, hashed on the way in. **The whole of making a book.**
 *
 * There is no `POST /api/v1/books`; this one request writes the `books` row and enqueues
 * `ingest.book`, and this envelope is what it answers with. The screen navigates to `book.id`.
 *
 * ⚠️ It is deliberately typed as the ENVELOPE and not as the inner `{sha,bytes,ext}`. Typing it as
 * the inner object is what let `Upload.tsx` read `up.sha` (which is `upload.sha`) as `undefined` and
 * post it to a route that does not exist — a green typecheck on both sides, because the body crosses
 * the network as `unknown` and is asserted.
 */
export interface UploadResult {
  /** The created book, or the existing same-source book when `duplicate` is true. */
  book: BookMeta | null;
  upload: { sha: string; bytes: number; ext: string };
  /** The queued `ingest.book` job. Null when no duplicate work was created. */
  jobId: string | null;
  /** True when these exact bytes were already on this account's live shelf. */
  duplicate?: boolean;
}

export type JobStatus = "queued" | "running" | "done" | "failed" | "cancelled";

/** A row of `jobs` (§9), as the UI sees it. */
export interface JobRow {
  id: string;
  kind: string;
  lane: JobLane;
  priority: number;
  status: JobStatus;
  bookId: string | null;
  attempts: number;
  maxAttempts: number;
  /** Nullable on purpose. A missing count renders as "—", never as 0% — absent ≠ zero (LAW 9). */
  tasksDone: number | null;
  tasksTotal: number | null;
  progress: number | null;
  message: string | null;
  error: string | null;
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
}

/**
 * `GET /api/v1/editions/:id/report` — the ingest report.
 *
 * §3.5: "Everything dropped is counted in the ingest report", and §3 calls it a **product surface,
 * not a log** — when an ingest looks wrong the first question is always *what did you throw away?*
 */
export interface IngestReport {
  editionId: string;
  source?: { ext: string; bytes: number; sha?: string };
  counts: Partial<Record<ReportCount, number>>;
  /**
   * Either a list of rows, or a `{category: count}` map — `worker/src/ingest/report.ts` may emit
   * whichever is natural for it. `droppedRows()` in Structure.tsx normalises both; the screen must
   * not be the thing that breaks over a shape.
   */
  dropped?: DroppedRow[] | Record<string, number>;
  warnings?: string[];
  toolVersions?: string;
  ms?: number;
}

export type ReportCount =
  | "chapters"
  | "blocks"
  | "sentences"
  | "words"
  | "chars"
  | "pages"
  | "images"
  | "notes"
  | "tables"
  | "verse";

export interface DroppedRow {
  what: string;
  count: number;
  why?: string;
}

/**
 * `GET /api/v1/editions/:id/structure` — chapters + roles + evidence + runner-ups (§3.4).
 *
 * ⚠️ **This is the SHARED half, not the whole payload.** P3's editor added `blockMode`, `pageCount`,
 * `narratedPages`, `suggestions`, and per-chapter `blockCount`/`firstSid`/`firstBlockId`/
 * `openingBlocks`, and those live in `client/src/structure/ChapterList.tsx` as `EditableStructure
 * extends Omit<StructureView, "chapters">`. That is an extension of this type and not a second copy
 * of it, which is the property that matters: the fields below have exactly one declaration, so a
 * change here is a type error there rather than a screen that quietly reads a field the route
 * stopped sending. Anything added to the route that BOTH screens need belongs here.
 */
export interface StructureView {
  editionId: string;
  bookId: string;
  structure: EditionStructure;
  bodyStartSid: string | null;
  bodyStartConfidence: number | null;
  bodyStartReason: string | null;
  structureConfirmedAt: number | null;
  /** Human-selected real chapter one/two for unattended library narration. Null keeps it held. */
  openingChapterOrds: number[] | null;
  needsReview: boolean;
  /** Every signal that was evaluated, scored, in the order §3.4 lists them. */
  evidence: EvidenceRow[];
  /** The chapters that lost, with their scores — §3.4 requires the scores be shown. */
  runnerUps: { chapterOrd: number; title: string; score: number; reason?: string }[];
  chapters: StructureChapter[];
}

export interface EvidenceRow {
  /** The signal number from §3.4's table, when the detector emits one. */
  signal?: number;
  name: string;
  score: number;
  chapterOrd?: number | null;
  detail?: string;
}

/** A `Chapter` plus the 140-character opening §3.4 requires — reading the openings is how a human fixes this. */
export interface StructureChapter extends Chapter {
  preview: string;
  /** True for the chapter containing `bodyStartSid`. */
  isBodyStart?: boolean;
}

// ─── typed calls ─────────────────────────────────────────────────────────────

export const coverUrl = (bookId: string, w: number): string =>
  `${BASE}/covers/${encodeURIComponent(bookId)}.webp?w=${w}`;

export const abApi = {
  session: () => api.get<SessionResponse>("/session"),
  me: () => api.get<MeResponse>("/me"),
  signIn: (who: string, password: string) => api.post<SessionResponse>("/auth/login", { who, password }),
  signUp: (body: { email: string; handle: string; password: string; invite: string }) =>
    api.post<SessionResponse>("/auth/signup", body),
  signOut: () => api.post<void>("/auth/logout"),

  shelf: (scope: ShelfScope, sort: ShelfSort, q: string) =>
    api.get<ShelfResponse>(
      `/shelf?scope=${scope}&sort=${sort}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
    ),

  /**
   * §3.4's gate, for a shelf. `dryRun` prices it; `dryRun: false` spends it.
   *
   * Both halves go through the same route on purpose: a preview computed by a different code path
   * than the commit is a quote that can be wrong, and the whole reason this exists is that LAW 10's
   * three-pages-per-book has to be a number somebody agreed to.
   */
  confirmMany: (editionIds: string[], minConfidence: number, dryRun: boolean) =>
    api.post<{ books: { editionId: string; bookId: string; title: string; confidence?: number }[]; pages: number; estGpuSec: number; confirmed?: number; skipped: { editionId: string; why: string }[] }>(
      "/editions/confirm-many",
      { editionIds, minConfidence, dryRun },
    ),

  book: (bookId: string) => api.get<BookDetail>(`/books/${encodeURIComponent(bookId)}`),
  deleteBook: (bookId: string) => api.del<void>(`/books/${encodeURIComponent(bookId)}`),
  patchBook: (bookId: string, patch: { visibility?: BookVisibility; title?: string; author?: string | null }) =>
    api.patch<PatchBookResult>(`/books/${encodeURIComponent(bookId)}`, patch),

  shares: (bookId: string) => api.get<SharesResponse>(`/books/${encodeURIComponent(bookId)}/shares`),
  createShare: (bookId: string, body: CreateShareBody) =>
    api.post<{ link: ShareLink }>(`/books/${encodeURIComponent(bookId)}/shares`, body),
  /**
   * ⚠️ The token is a **path segment**, and `encodeURIComponent` is not decoration: a token that
   * failed the server's shape check still has to arrive as one segment, or a malformed one becomes a
   * different route and answers something other than the house 404.
   */
  revokeShare: (token: string) => api.del<{ ok: true; revokedAt?: number }>(`/shares/${encodeURIComponent(token)}`),
  start: (bookId: string) => api.get<StartPoint>(`/books/${encodeURIComponent(bookId)}/start`),

  jobs: (bookId: string) => api.get<{ jobs: JobRow[] }>(`/jobs?book=${encodeURIComponent(bookId)}`),
  cancelJob: (jobId: string) => api.post<void>(`/jobs/${encodeURIComponent(jobId)}/cancel`),

  report: (editionId: string) => api.get<IngestReport>(`/editions/${encodeURIComponent(editionId)}/report`),
  structure: (editionId: string) =>
    api.get<StructureView>(`/editions/${encodeURIComponent(editionId)}/structure`),
};
